import {
    SparklesIcon,
    PlayIcon,
    StopIcon,
    ArrowPathIcon,
    BookmarkIcon,
} from '../../automationStudioIcons';
import { Selectbox } from '../../../FormComponent';
import ButtonComponent from '../../../customButton';
import AIModelRuntimeSelector from '../../../Select/llmModelSelector/AIModelRuntimeSelector';
import styles from '../../../../styles/automationStudio/automationStudio.module.scss';
import { LABELS } from '../../content';

export default function ActionBar({
    selectedEnvironment,
    selectedAIModel,
    isGenerating,
    isSavedExecutionRecord,
    replayExecuting,
    isEmptyState,
    hasInstructions,
    environments = [],
    onEnvironmentChange,
    onModelChange,
    canSelectAiModel = true,
    studioFeatureKey,
    trialOrgSkip = false,
    projectId,
    onGenerate,
    onReplay,
    onSaveAndReplay,
    onStopExecution,
    onSave,
    onReset,
    isSaving = false,
    canSave = false,
    hasUnsavedChanges = false,
    hasAISteps = false,
    hasSelectedTestCase = false,
    instructionsSyncedWithSteps = true,
    planIsDirty = false,
}) {
    const isBusy = isGenerating || replayExecuting || isSaving;
    const canGenerate = hasInstructions && !isBusy;
    // Plain Replay: plan must match last save (no unsaved step edits).
    const planInSync = instructionsSyncedWithSteps && !planIsDirty;
    const canReplayBase = hasAISteps && planInSync && !isBusy;
    const canReplayWithLinkedTestCase = canReplayBase && hasSelectedTestCase;
    const canSaveEnabled =
        canSave ||
        (hasUnsavedChanges &&
            !isSavedExecutionRecord &&
            hasAISteps);
    // Save & Replay: allow plan-only edits (isDirty); still block when instruction text drifted.
    const showSaveAndReplay =
        canSaveEnabled && hasAISteps && instructionsSyncedWithSteps && !isBusy;
    const replayButtonEnabled = showSaveAndReplay || canReplayWithLinkedTestCase;
    const isSaveAndReplayLoading = Boolean(showSaveAndReplay && (isSaving || replayExecuting));

    return (
        <div className={styles.actionBar} data-testid="automation-studio-action-bar">
            <div className={styles.actionBarLeft}>
                <Selectbox
                    options={environments}
                    label={LABELS.SELECT_ENVIRONMENT}
                    size="small"
                    value={selectedEnvironment || ''}
                    onChange={(e) => onEnvironmentChange?.(e.target.value)}
                    openUpward
                    required={false}
                    disabled={isBusy}
                />
                <AIModelRuntimeSelector
                    featureKey={studioFeatureKey}
                    trialOrgSkip={trialOrgSkip}
                    canSelectAiModel={canSelectAiModel}
                    projectId={projectId}
                    label={LABELS.SELECT_AI_MODEL}
                    size="small"
                    openUpward
                    className={styles.actionBarAiModel}
                    disabled={isBusy || !canSelectAiModel}
                    readOnly={!canSelectAiModel}
                    required
                    onModelChange={onModelChange}
                />
            </div>

            {!isEmptyState && (
                <div className={styles.actionBarRight}>
                    <ButtonComponent
                        ctaText={isGenerating ? LABELS.GENERATING_AI_STEPS : LABELS.GENERATE_AI_STEPS}
                        variant="primary"
                        className={`white bg-blue600 ${isGenerating ? styles.generatingButton : ''}`}
                        startIcon={<SparklesIcon className="icon white" />}
                        onClick={onGenerate}
                        disabled={!canGenerate}
                        loading={isGenerating}
                    />
                    <ButtonComponent
                        ctaText={
                            isSaving && showSaveAndReplay
                                ? LABELS.SAVING
                                : replayExecuting
                                  ? LABELS.REPLAYING
                                  : showSaveAndReplay
                                    ? LABELS.SAVE_AND_REPLAY
                                    : LABELS.REPLAY
                        }
                        variant="primary"
                        className={showSaveAndReplay ? 'white bg-green600' : 'white bg-primary'}
                        startIcon={<PlayIcon className="icon white" />}
                        onClick={() => {
                            if (showSaveAndReplay) onSaveAndReplay?.();
                            else onReplay?.();
                        }}
                        disabled={!replayButtonEnabled || isSaving}
                        loading={isSaveAndReplayLoading || replayExecuting}
                    />
                    <ButtonComponent
                        type="iconbutton"
                        variant="outline"
                        startIcon={<StopIcon className="icon red600" />}
                        onClick={onStopExecution}
                        disabled={!isGenerating && !replayExecuting}
                        tooltip={LABELS.STOP_EXECUTION}
                    />
                    <ButtonComponent
                        ctaText={isSaving ? LABELS.SAVING : LABELS.SAVE}
                        variant="primary"
                        className="white bg-green600"
                        startIcon={<BookmarkIcon className="icon white" />}
                        onClick={onSave}
                        disabled={!canSaveEnabled || isBusy}
                        loading={isSaving}
                    />
                    <ButtonComponent
                        ctaText={LABELS.RESET}
                        variant="secondary"
                        className="slate900 bg-slate200"
                        startIcon={<ArrowPathIcon className="icon slate900" />}
                        onClick={onReset}
                        disabled={isEmptyState || isBusy}
                    />
                </div>
            )}
        </div>
    );
}
